import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi, UTCTimestamp, AreaData, LineData, CandlestickData, MouseEventParams, PriceScaleMode, CrosshairMode, IPriceLine } from 'lightweight-charts';
import { Info, TrendingUp, Calendar, ArrowUpRight, ArrowDownRight, ShieldCheck, MousePointer2, ChevronDown, Coins, Activity, CandlestickChart, Percent, Scaling, Ruler, Maximize2, Minimize2, Radio, LineChart } from 'lucide-react';
import { Transaction, Candle } from '../types';
import { fetchBinanceKlines } from '../services/marketService';

interface Stats {
  value: number;
  cost: number;
  pnl: number;
  pnlPerc: number;
  changeMonth: number;
  changeMonthPerc: number;
  changeWeek?: number;
  changeWeekPerc?: number;
  changeYear?: number;
  changeYearPerc?: number;
  change24h?: number;
  change24hPerc?: number;
  time?: UTCTimestamp;
}

interface PortfolioHistoryChartProps {
  transactions: Transaction[];
  theme: 'dark' | 'light';
  symbolMetrics?: any[];
  activeTab?: string;
  onStatsUpdate?: (stats: Stats) => void;
  isMaximized?: boolean;
  onToggleMaximize?: (val: boolean) => void;
}

interface HistoricalSnapshot {
  time: UTCTimestamp;
  value: number;
  open: number;
  high: number;
  low: number;
  cost: number;
}

interface TooltipPosition {
  x: number;
  y: number;
}

interface MeasurePoint {
  price: number;
  time: number;
  x: number;
  y: number;
}

const PortfolioHistoryChart: React.FC<PortfolioHistoryChartProps> = ({ transactions, theme, symbolMetrics, activeTab, onStatsUpdate, isMaximized, onToggleMaximize }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const valueSeriesRef = useRef<ISeriesApi<any> | null>(null);
  const costSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const activeCostPriceLineRef = useRef<IPriceLine | null>(null);
  
  const measureStartLineRef = useRef<IPriceLine | null>(null);
  const measureEndLineRef = useRef<IPriceLine | null>(null);

  const cachedHistoryRef = useRef<HistoricalSnapshot[]>([]);
  const prevTransactionsRef = useRef<Transaction[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<'7d' | '30d' | '90d' | '1y' | '3y' | '5y' | 'all'>('3y');
  const [chartType, setChartType] = useState<'area' | 'candlestick'>('area');
  const [isLogarithmic, setIsLogarithmic] = useState(false);
  const [currentStats, setCurrentStats] = useState<Stats>({ value: 0, cost: 0, pnl: 0, pnlPerc: 0, changeMonth: 0, changeMonthPerc: 0, changeWeek: 0, changeWeekPerc: 0, changeYear: 0, changeYearPerc: 0, change24h: 0, change24hPerc: 0 });
  const [hoverStats, setHoverStats] = useState<Stats | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);

  const [isMeasureMode, setIsMeasureMode] = useState(false);
  const [measureStart, setMeasureStart] = useState<MeasurePoint | null>(null);
  const [measureCurrent, setMeasureCurrent] = useState<MeasurePoint | null>(null);

  const isMeasureModeRef = useRef(isMeasureMode);
  const measureStartRef = useRef(measureStart);

  useEffect(() => {
    isMeasureModeRef.current = isMeasureMode;
    measureStartRef.current = measureStart;
  }, [isMeasureMode, measureStart]);

  const COLORS = {
    emerald: '#10b981',
    emeraldTransparent: 'rgba(16, 185, 129, 0.2)',
    emeraldZero: 'rgba(16, 185, 129, 0.0)',
    costLine: theme === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
    text: theme === 'dark' ? '#94a3b8' : '#64748b',
    grid: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
  };

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const width = chartContainerRef.current.clientWidth;
    const height = chartContainerRef.current.clientHeight || 380;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: COLORS.text,
        fontFamily: "'Glegoo', serif",
      },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.2, bottom: 0.1 },
        mode: isLogarithmic ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal,
      },
      timeScale: {
        borderVisible: true,
        timeVisible: true,
        borderColor: theme === 'dark' ? '#262626' : '#e2e8f0',
      },
      crosshair: {
        mode: CrosshairMode.Normal, 
        vertLine: { color: COLORS.emerald, width: 1, style: 3, labelVisible: true },
        horzLine: { color: COLORS.emerald, width: 1, style: 3, labelVisible: true },
      },
      width,
      height,
    });

    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
        if (!entries || entries.length === 0 || !entries[0].contentRect) return;
        const { width, height } = entries[0].contentRect;
        if (width > 0 && height > 0 && chartRef.current) {
            try {
                chartRef.current.applyOptions({ width, height });
            } catch (e) {}
        }
    });
    resizeObserver.observe(chartContainerRef.current);

    const handleCrosshairMove = (param: MouseEventParams) => {
      const vSeries = valueSeriesRef.current;
      const cSeries = costSeriesRef.current;

      if (isMeasureModeRef.current && measureStartRef.current && param.point && vSeries) {
         try {
             const price = vSeries.coordinateToPrice(param.point.y);
             if (price !== null) {
                setMeasureCurrent({
                    price,
                    time: param.time as number,
                    x: param.point.x,
                    y: param.point.y
                });
             }
         } catch (e) {}
      }

      if (param.point === undefined || !param.time) {
        setHoverStats(null);
        setTooltipPos(null);
        if (activeCostPriceLineRef.current && cSeries) {
            try { cSeries.removePriceLine(activeCostPriceLineRef.current); } catch (e) {}
            activeCostPriceLineRef.current = null;
        }
      } else if (vSeries && cSeries) {
        try {
            const valData = param.seriesData.get(vSeries) as any;
            const costData = param.seriesData.get(cSeries) as any;
            
            if (valData && costData) {
              const val = valData.value !== undefined ? valData.value : (valData.close !== undefined ? valData.close : 0);
              const cost = costData.value !== undefined ? costData.value : (costData.close !== undefined ? costData.close : 0);
              
              if (!isMeasureModeRef.current) {
                setHoverStats({
                  value: val,
                  cost: cost,
                  pnl: val - cost,
                  pnlPerc: cost > 0 ? ((val - cost) / cost) * 100 : 0,
                  changeMonth: 0,
                  changeMonthPerc: 0,
                  changeWeek: 0,
                  changeWeekPerc: 0,
                  changeYear: 0,
                  changeYearPerc: 0,
                  time: param.time as UTCTimestamp
                });
                setTooltipPos({ x: param.point.x, y: param.point.y });
              }

              if (activeCostPriceLineRef.current) {
                 try { cSeries.removePriceLine(activeCostPriceLineRef.current); } catch (e) {}
                 activeCostPriceLineRef.current = null;
              }
              
              activeCostPriceLineRef.current = cSeries.createPriceLine({
                price: cost,
                color: theme === 'dark' ? '#94a3b8' : '#64748b',
                lineWidth: 1,
                lineStyle: 2,
                axisLabelVisible: true,
                title: '', 
              });
            }
        } catch (e) {}
      }
    };

    const handleClick = (param: MouseEventParams) => {
        if (!isMeasureModeRef.current || !valueSeriesRef.current || !param.point) return;
        try {
            const price = valueSeriesRef.current.coordinateToPrice(param.point.y);
            if (price === null) return;
            if (measureStartRef.current) {
                setMeasureStart(null);
                setMeasureCurrent(null);
                setIsMeasureMode(false);
            } else {
                setMeasureStart({ price, time: param.time as number, x: param.point.x, y: param.point.y });
            }
        } catch (e) {}
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);
    chart.subscribeClick(handleClick);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
          const currentChart = chartRef.current;
          chartRef.current = null;
          valueSeriesRef.current = null;
          costSeriesRef.current = null;
          activeCostPriceLineRef.current = null;
          measureStartLineRef.current = null;
          measureEndLineRef.current = null;

          try {
              currentChart.unsubscribeCrosshairMove(handleCrosshairMove);
              currentChart.unsubscribeClick(handleClick);
              currentChart.remove();
          } catch (e) {}
      }
    };
  }, [theme]);

  // Fix: Ensure chart fits content when visibility changes or maximize happens
  useEffect(() => {
    if (activeTab === 'history' && chartRef.current) {
        const timer = setTimeout(() => {
            try {
                chartRef.current?.timeScale().fitContent();
            } catch (e) {}
        }, 100);
        return () => clearTimeout(timer);
    }
  }, [activeTab, isMaximized]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    try {
        if (valueSeriesRef.current) {
            try { chart.removeSeries(valueSeriesRef.current); } catch(e) {}
        }
        if (costSeriesRef.current) {
            try { chart.removeSeries(costSeriesRef.current); } catch(e) {}
        }

        if (chartType === 'candlestick') {
          valueSeriesRef.current = (chart as any).addCandlestickSeries({
            upColor: '#089981', downColor: '#f23645', borderVisible: false, wickUpColor: '#089981', wickDownColor: '#f23645',
          });
        } else {
          valueSeriesRef.current = (chart as any).addAreaSeries({
            lineColor: COLORS.emerald, topColor: COLORS.emeraldTransparent, bottomColor: COLORS.emeraldZero, lineWidth: 3,
            priceFormat: { type: 'price', precision: 0, minMove: 1 }, title: 'Valor Mercado'
          });
        }

        costSeriesRef.current = (chart as any).addLineSeries({
          color: COLORS.costLine, lineWidth: 2, lineStyle: 2, 
          priceFormat: { type: 'price', precision: 0, minMove: 1 }, title: '$ Expuesto'
        });

        const transactionsChanged = JSON.stringify(transactions) !== JSON.stringify(prevTransactionsRef.current);
        if (transactionsChanged || cachedHistoryRef.current.length === 0) {
          prevTransactionsRef.current = transactions;
          generateHistoryData();
        } else {
          applyRangeData(cachedHistoryRef.current);
        }
    } catch (e) {}
  }, [chartType, transactions, range, theme]);

  useEffect(() => {
    if (chartRef.current) {
      try {
        chartRef.current.priceScale('right').applyOptions({
          mode: isLogarithmic ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal,
        });
      } catch (e) {}
    }
  }, [isLogarithmic]);

  useEffect(() => {
    const vSeries = valueSeriesRef.current;
    const cSeries = costSeriesRef.current;
    if (!symbolMetrics || symbolMetrics.length === 0 || cachedHistoryRef.current.length === 0 || !vSeries || !cSeries || !chartRef.current) return;
    const history = cachedHistoryRef.current;
    const lastPoint = history[history.length - 1];
    const prevDay = history.length > 1 ? history[history.length - 2] : null;
    if (!lastPoint) return;

    const liveValue = symbolMetrics.reduce((acc, m) => acc + (m.currentVal || 0), 0);
    const liveCost = symbolMetrics.reduce((acc, m) => acc + ((m.totalBuy || 0) - (m.totalSell || 0)), 0);
    const updatedTime = lastPoint.time;

    try {
        if (chartType === 'area') {
            (vSeries as ISeriesApi<'Area'>).update({ time: updatedTime, value: liveValue });
        } else {
            (vSeries as ISeriesApi<'Candlestick'>).update({ time: updatedTime, open: lastPoint.open, high: Math.max(lastPoint.high, liveValue), low: Math.min(lastPoint.low, liveValue), close: liveValue });
        }
        cSeries.update({ time: updatedTime, value: liveCost });
    } catch (e) {}

    if (!hoverStats) {
        const prevMonth = history[Math.max(0, history.length - 31)];
        const prevWeek = history[Math.max(0, history.length - 8)];
        const prevYear = history[Math.max(0, history.length - 366)];
        
        const changeMonth = prevMonth ? (liveValue - prevMonth.value) : 0;
        const changeMonthPerc = (prevMonth && prevMonth.value > 0) ? (changeMonth / prevMonth.value) * 100 : 0;
        const changeWeek = prevWeek ? (liveValue - prevWeek.value) : 0;
        const changeWeekPerc = (prevWeek && prevWeek.value > 0) ? (changeWeek / prevWeek.value) * 100 : 0;
        const changeYear = prevYear ? (liveValue - prevYear.value) : 0;
        const changeYearPerc = (prevYear && prevYear.value > 0) ? (changeYear / prevYear.value) * 100 : 0;
        const change24h = prevDay ? (liveValue - prevDay.value) : 0;
        const change24hPerc = (prevDay && prevDay.value > 0) ? (change24h / prevDay.value) * 100 : 0;
        
        const liveStats: Stats = {
            value: liveValue, cost: liveCost, pnl: liveValue - liveCost, pnlPerc: liveCost > 0 ? ((liveValue - liveCost) / liveCost) * 100 : 0,
            changeMonth, changeMonthPerc, changeWeek, changeWeekPerc, changeYear, changeYearPerc, change24h, change24hPerc, time: updatedTime
        };
        setCurrentStats(liveStats);
        if (onStatsUpdate) onStatsUpdate(liveStats);
    }
  }, [symbolMetrics, chartType, hoverStats]);

  const applyRangeData = (fullHistory: HistoricalSnapshot[]) => {
    if (!chartRef.current || !valueSeriesRef.current || !costSeriesRef.current) return;
    let filtered = fullHistory;
    if (range === '7d') filtered = fullHistory.slice(-7);
    else if (range === '30d') filtered = fullHistory.slice(-30);
    else if (range === '90d') filtered = fullHistory.slice(-90);
    else if (range === '1y') filtered = fullHistory.slice(-365);
    else if (range === '3y') filtered = fullHistory.slice(-1095);
    else if (range === '5y') filtered = fullHistory.slice(-1825);

    try {
        if (chartType === 'candlestick') {
          valueSeriesRef.current.setData(filtered.map(d => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.value })));
        } else {
          valueSeriesRef.current.setData(filtered.map(d => ({ time: d.time, value: d.value })));
        }
        costSeriesRef.current.setData(filtered.map(d => ({ time: d.time, value: d.cost })));

        const last = fullHistory[fullHistory.length - 1];
        const prevMonth = fullHistory[Math.max(0, fullHistory.length - 31)];
        const prevWeek = fullHistory[Math.max(0, fullHistory.length - 8)];
        const prevYear = fullHistory[Math.max(0, fullHistory.length - 366)];
        const prevDay = fullHistory.length > 1 ? fullHistory[fullHistory.length - 2] : null;
        
        if (last) {
          const changeMonth = prevMonth ? (last.value - prevMonth.value) : 0;
          const changeMonthPerc = (prevMonth && prevMonth.value > 0) ? (changeMonth / prevMonth.value) * 100 : 0;
          const changeWeek = prevWeek ? (last.value - prevWeek.value) : 0;
          const changeWeekPerc = (prevWeek && prevWeek.value > 0) ? (changeWeek / prevWeek.value) * 100 : 0;
          const changeYear = prevYear ? (last.value - prevYear.value) : 0;
          const changeYearPerc = (prevYear && prevYear.value > 0) ? (changeYear / prevYear.value) * 100 : 0;
          const change24h = prevDay ? (last.value - prevDay.value) : 0;
          const change24hPerc = (prevDay && prevDay.value > 0) ? (change24h / prevDay.value) * 100 : 0;
          
          const newStats = { value: last.value, cost: last.cost, pnl: last.value - last.cost, pnlPerc: last.cost > 0 ? ((last.value - last.cost) / last.cost) * 100 : 0, changeMonth, changeMonthPerc, changeWeek, changeWeekPerc, changeYear, changeYearPerc, change24h, change24hPerc };
          setCurrentStats(newStats);
          if (onStatsUpdate) onStatsUpdate(newStats);
        }
        
        if (chartContainerRef.current && chartContainerRef.current.clientWidth > 0 && chartContainerRef.current.clientHeight > 0) {
            chartRef.current.timeScale().fitContent();
        }
    } catch (e) {}
    setLoading(false);
  };

  const generateHistoryData = async () => {
    if (transactions.length === 0) {
      if (valueSeriesRef.current) valueSeriesRef.current.setData([]);
      if (costSeriesRef.current) costSeriesRef.current.setData([]);
      cachedHistoryRef.current = [];
      const zeroStats = { value: 0, cost: 0, pnl: 0, pnlPerc: 0, changeMonth: 0, changeMonthPerc: 0, changeWeek: 0, changeWeekPerc: 0, changeYear: 0, changeYearPerc: 0, change24h: 0, change24hPerc: 0 };
      setCurrentStats(zeroStats);
      if (onStatsUpdate) onStatsUpdate(zeroStats);
      setLoading(false); 
      return; 
    }
    setLoading(true);
    try {
      const uniqueTickets: string[] = Array.from(new Set(transactions.map(t => t.ticket)));
      const minTimestamp = Math.min(...transactions.map(t => t.timestamp));
      const historyPromises = uniqueTickets.map((ticket: string) => {
        const sampleTx = transactions.find(t => t.ticket === ticket);
        const category = (sampleTx?.category || 'criptomoneda').toLowerCase();
        const assetType = category === 'acciones' ? 'stocks' : 'crypto';
        return fetchBinanceKlines(ticket, '1d', undefined, assetType);
      });
      const allKlines = await Promise.all(historyPromises);
      
      if (!chartRef.current) return;

      const symbolHistoryMap: Record<string, Candle[]> = {};
      uniqueTickets.forEach((ticket: string, i: number) => { symbolHistoryMap[ticket] = allKlines[i]; });

      const startDay = new Date(minTimestamp * 1000);
      startDay.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const fullHistory: HistoricalSnapshot[] = [];
      let currentDay = new Date(startDay);
      const runningInventory: Record<string, number> = {};
      let runningNetCost = 0;
      const sortedTransactions = [...transactions].sort((a, b) => a.timestamp - b.timestamp);
      let transIndex = 0;

      while (currentDay <= today) {
        const dayStartTs = Math.floor(currentDay.getTime() / 1000);
        const dayEndTs = dayStartTs + 86399;
        while (transIndex < sortedTransactions.length && sortedTransactions[transIndex].timestamp <= dayEndTs) {
          const t = sortedTransactions[transIndex];
          const isBuy = t.status.toLowerCase().includes('compra') || t.status.toLowerCase().includes('buy');
          if (isBuy) { runningInventory[t.ticket] = (runningInventory[t.ticket] || 0) + t.quantity; runningNetCost += t.total; }
          else { runningInventory[t.ticket] = (runningInventory[t.ticket] || 0) - t.quantity; runningNetCost -= t.total; }
          transIndex++;
        }
        let dayValue = 0, dayOpen = 0, dayHigh = 0, dayLow = 0;
        Object.entries(runningInventory).forEach(([ticket, qty]) => {
          if (qty <= 0.00000001) return;
          const klines = symbolHistoryMap[ticket];
          if (!klines) return;
          const kline = klines.find(k => k.time === dayStartTs) || [...klines].reverse().find(k => k.time < dayStartTs);
          if (kline) { dayValue += qty * kline.close; dayOpen += qty * kline.open; dayHigh += qty * kline.high; dayLow += qty * kline.low; }
        });
        if (dayValue === 0) { dayOpen = 0; dayHigh = 0; dayLow = 0; }
        fullHistory.push({ time: dayStartTs as UTCTimestamp, value: dayValue, open: dayOpen, high: dayHigh, low: dayLow, cost: Math.max(0, runningNetCost) });
        currentDay.setDate(currentDay.getDate() + 1);
      }
      cachedHistoryRef.current = fullHistory;
      applyRangeData(fullHistory);
    } catch (error) { console.error("Error reconstruyendo historial:", error); } finally { setLoading(false); }
  };

  const getMeasureDiff = () => { if (!measureStart || !measureCurrent) return 0; return measureCurrent.price - measureStart.price; };
  const getMeasurePerc = () => { if (!measureStart || !measureCurrent) return 0; return (getMeasureDiff() / measureStart.price) * 100; };
  const getMeasureDays = () => { if (!measureStart || !measureCurrent) return 0; return Math.round(Math.abs(measureCurrent.time - measureStart.time) / 86400); };
  const displayStats = hoverStats || currentStats;

  const sidebarButtonClass = `p-2 rounded-lg transition-all backdrop-blur-md shadow-sm border ${theme === 'dark' ? 'bg-[#1a1c22]/80 border-[#363a45] hover:bg-white/10 text-slate-300' : 'bg-white/80 border-slate-200 hover:bg-slate-50 text-slate-600'}`;
  const sidebarButtonActiveClass = `p-2 rounded-lg transition-all shadow-md border bg-indigo-600 border-indigo-500 text-white`;

  const handleToggleChartType = () => {
    setChartType(prev => prev === 'area' ? 'candlestick' : 'area');
  };

  return (
    <div className={`flex flex-col ${isMaximized ? 'h-full w-full p-2 overflow-hidden' : 'h-full animate-in fade-in duration-700'}`}>
      <div className={`flex-1 flex flex-col rounded-2xl border ${theme === 'dark' ? 'bg-[#161616] border-[#262626]' : 'bg-white border-slate-100'} shadow-2xl relative transition-all duration-300 ${isMaximized ? 'p-4' : 'p-6'} overflow-hidden`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-8 space-y-4 sm:space-y-0 flex-shrink-0 relative z-30">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20"><TrendingUp size={24} className="text-emerald-500" /></div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className={`text-xl font-black uppercase tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'} flex items-center`}>Rendimiento Histórico</h3>
                <div className="flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 space-x-1">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>
                    <span className="text-[9px] font-black text-emerald-500 tracking-wider">LIVE</span>
                </div>
                <div className="group relative ml-1"><Info size={14} className="text-slate-500 cursor-help" />
                  <div className={`absolute bottom-full mb-3 left-0 w-80 p-5 rounded-2xl ${theme === 'dark' ? 'bg-[#1e222d] border-[#363a45]' : 'bg-white border-slate-200'} border text-[11px] leading-relaxed text-slate-400 shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                    <p className="font-black mb-3 text-emerald-500 uppercase tracking-widest text-xs">Especificación Técnica</p>
                    <p className="mb-3">Este gráfico muestra la evolución del patrimonio basada en el <strong>Valor de Mercado</strong> y el <strong>$ Expuesto</strong>.</p>
                    <p className="mb-3">El <span className="text-emerald-500 font-bold">Valor de Mercado</span> se actualiza en <strong className="text-white">Tiempo Real</strong> calculando el valor actual de todos los activos.</p>
                    <p className="mb-3">El <span className="text-emerald-500 font-bold">$ Expuesto</span> representa la inversión neta (Compras - Ventas) acumulada hasta esa fecha.</p>
                    <div className="flex items-center space-x-2 pt-2 border-t border-white/5"><ShieldCheck size={12} className="text-emerald-500" /><span className="text-[9px] uppercase font-bold text-emerald-500">Motor de Snapshot Diario + Live Feed</span></div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-5 mt-2">
                <div className="flex items-center space-x-2"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div><span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Valor Mercado</span></div>
                <div className="flex items-center space-x-2"><div className="w-4 h-0.5 bg-slate-500 rounded-full opacity-50"></div><span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">$ Expuesto</span></div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-4">
            <div className="flex items-center space-x-3">
              {!!hoverStats && !isMeasureMode && (
                  <div className="flex items-center space-x-2 bg-indigo-500/10 px-3 py-1.5 rounded-lg border border-indigo-500/20 animate-in fade-in zoom-in-95 duration-200">
                    <MousePointer2 size={12} className="text-indigo-400" />
                    <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{hoverStats.time ? new Date(hoverStats.time * 1000).toLocaleDateString() : ''}</span>
                  </div>
              )}
              <div className={`flex p-1 rounded-xl border ${theme === 'dark' ? 'bg-black/20 border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                {[{ id: '7d', label: '7D' }, { id: '30d', label: '1M' }, { id: '90d', label: '3M' }, { id: '1y', label: '1A' }, { id: '3y', label: '3A' }, { id: '5y', label: '5A' }, { id: 'all', label: 'TODO' }].map(btn => (
                  <button key={btn.id} onClick={() => setRange(btn.id as any)} className={`px-4 py-2 text-[10px] font-black rounded-lg transition-all uppercase tracking-widest ${range === btn.id ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'text-slate-500 hover:text-slate-300'}`}>{btn.label}</button>
                ))}
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={handleToggleChartType} 
                  className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 ${theme === 'dark' ? 'bg-[#1a1c22]/80 border border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  title={chartType === 'area' ? "Cambiar a Velas" : "Cambiar a Área"}
                >
                  {chartType === 'area' ? <CandlestickChart size={18} /> : <Activity size={18} />}
                </button>
                <button 
                  onClick={() => onToggleMaximize?.(!isMaximized)} 
                  className={`p-2 rounded-xl transition-all shadow-lg active:scale-95 ${theme === 'dark' ? 'bg-[#1a1c22]/80 border border-[#363a45] text-slate-300 hover:bg-white/10' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  title={isMaximized ? "Minimizar" : "Maximizar"}
                >
                  {isMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
              </div>
            </div>
            {!loading && (
              <div className="text-right">
                <span className="block text-[9px] font-black text-slate-500 uppercase tracking-[0.2em] mb-0.5">{!!hoverStats && !isMeasureMode ? 'Ganancia en Fecha' : 'Ganancia Acumulada'}</span>
                <div className={`flex items-center justify-end font-mono font-black text-lg ${displayStats.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {displayStats.pnl >= 0 ? <ArrowUpRight size={18} className="mr-1" /> : <ArrowDownRight size={18} className="mr-1" />}
                  ${Math.abs(displayStats.pnl || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  <span className="ml-2 text-[11px] bg-white/5 px-2 py-0.5 rounded-md border border-white/5">{displayStats.pnl >= 0 ? '+' : ''}{(displayStats.pnlPerc || 0).toFixed(1)}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={`flex-1 relative min-h-0 w-full overflow-hidden ${isMeasureMode ? 'cursor-crosshair' : ''}`}>
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#161616]/60 backdrop-blur-sm z-10 rounded-2xl border border-white/5">
              <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <div className="text-center"><span className="block text-[11px] font-black text-emerald-500 uppercase tracking-[0.3em] animate-pulse">Sincronizando Ciclos</span><span className="block text-[9px] text-slate-500 uppercase mt-1">Calculando balances históricos...</span></div>
            </div>
          )}

          <div className="absolute top-4 left-4 z-40 flex flex-col space-y-2">
            <button onClick={() => { setIsMeasureMode(!isMeasureMode); setMeasureStart(null); setMeasureCurrent(null); }} className={isMeasureMode ? sidebarButtonActiveClass : sidebarButtonClass} title="Regla"><Ruler size={18} /></button>
            <button onClick={() => setIsLogarithmic(!isLogarithmic)} className={sidebarButtonClass} title="Escala Logarítmica">
               <span className="text-[9px] font-black">{isLogarithmic ? 'LOG' : 'LIN'}</span>
            </button>
          </div>

          <div ref={chartContainerRef} className="w-full h-full" />

          {isMeasureMode && measureStart && measureCurrent && (
            <>
              <div className="absolute pointer-events-none border border-dashed border-[#2962ff] bg-[#2962ff]/10 z-40 transition-none" style={{ left: Math.min(measureStart.x, measureCurrent.x), top: Math.min(measureStart.y, measureCurrent.y), width: Math.abs(measureCurrent.x - measureStart.x), height: Math.abs(measureCurrent.y - measureStart.y) }} />
              <div className="absolute z-[100] pointer-events-none p-3 rounded-2xl shadow-2xl flex flex-col items-center justify-center text-center space-y-1 bg-[#2962ff] text-white min-w-[190px] -translate-x-1/2 -translate-y-full mb-4" style={{ left: (measureStart.x + measureCurrent.x) / 2, top: Math.min(measureStart.y, measureCurrent.y) - 5 }}>
                  <div className="text-[15px] font-black whitespace-nowrap leading-none tracking-tight">{getMeasureDiff() >= 0 ? '+' : ''}{getMeasureDiff().toLocaleString(undefined, { maximumFractionDigits: 2 })}<span className="ml-1 opacity-90">({getMeasurePerc() >= 0 ? '+' : ''}{getMeasurePerc().toFixed(2)}%)</span></div>
                  <div className="text-[11px] font-bold opacity-80 whitespace-nowrap tracking-wide flex items-center space-x-1.5"><span>{getMeasureDays()} días</span></div>
              </div>
            </>
          )}
          {tooltipPos && hoverStats && chartType === 'area' && !isMeasureMode && (
            <div className={`absolute z-[120] pointer-events-none p-4 rounded-xl border shadow-2xl backdrop-blur-md animate-in fade-in zoom-in-95 duration-150 flex flex-col space-y-2.5 min-w-[200px] ${theme === 'dark' ? 'bg-[#1a1c22]/90 border-[#363a45] text-slate-200' : 'bg-white/90 border-slate-200 text-slate-900'}`} style={{ left: tooltipPos.x + 15, top: tooltipPos.y - 80, transform: tooltipPos.x > (chartContainerRef.current?.clientWidth || 0) * 0.7 ? 'translateX(-110%)' : 'none' }}>
              <div className="flex items-center justify-between border-b border-white/5 pb-2 mb-1"><span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Snapshot Cartera</span><span className="text-[10px] font-mono opacity-60">{hoverStats.time ? new Date(hoverStats.time * 1000).toLocaleDateString() : ''}</span></div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px]"><span className="font-bold opacity-60">Valor Mercado</span><span className="font-black text-emerald-500">${(hoverStats.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                <div className="flex items-center justify-between text-[11px]"><span className="font-bold opacity-60">$ Expuesto</span><span className="font-black">${(hoverStats.cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
                <div className="pt-1.5 mt-1.5 border-t border-white/5 flex items-center justify-between text-[11px]"><span className="font-black uppercase tracking-tighter opacity-80">Ganancia Neta</span><span className={`font-black ${hoverStats.pnl >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{hoverStats.pnl >= 0 ? '+' : ''}{(hoverStats.pnl || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({(hoverStats.pnlPerc || 0).toFixed(1)}%)</span></div>
              </div>
            </div>
          )}
        </div>
        <div className={`mt-8 pt-6 border-t ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 flex-shrink-0`}>
          <div className="flex items-center space-x-3 bg-emerald-500/5 px-4 py-2 rounded-full border border-emerald-500/10"><Calendar size={12} className="text-emerald-500" /><span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{!!hoverStats ? 'Fecha Seleccionada' : 'Origen Cartera'}: {!!hoverStats ? (hoverStats.time ? new Date(hoverStats.time * 1000).toLocaleDateString() : '') : (transactions.length > 0 ? new Date(Math.min(...transactions.map(t => t.timestamp)) * 1000).toLocaleDateString() : '--/--/--')}</span></div>
          <div className="flex items-center space-x-8">
             <div className="text-right"><span className="block text-[8px] font-black text-slate-600 uppercase tracking-widest mb-0.5">VALOR MERCADO</span><span className={`text-sm font-black transition-all ${!!hoverStats ? 'text-emerald-400' : (theme === 'dark' ? 'text-white' : 'text-slate-900')}`}>${(displayStats.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
             <div className="w-[1px] h-8 bg-slate-800"></div>
             <div className="text-right"><span className="block text-[8px] font-black text-slate-600 uppercase tracking-widest mb-0.5">$ EXPUESTO</span><span className={`text-sm font-black transition-all ${!!hoverStats ? 'text-indigo-400' : (theme === 'dark' ? 'text-white' : 'text-slate-900')}`}>${(displayStats.cost || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioHistoryChart;